/**
 * @file as-app.ts
 *
 * Root application component for App Suite applications. Composed from
 * focused mixins; this file retains only app-level identity, auth, shadow DOM
 * setup, and the cross-cutting button-click orchestrator.
 *
 * Mixin chain (inner → outer):
 *   LocalizationMixin → VersionMixin → InitQueueMixin → ModalMixin →
 *   ErrorMixin → ProjectMixin → HistoryMixin → MeshMixin → FileDropMixin → PathsMixin → BusyMixin → GrabCADMixin → AsApp
 *
 * Sub-concerns delegated to sibling modules:
 *   app-config.ts      — app info / available-apps queries
 *   localization.ts    — language files and <as-text> subscribers
 *   modal-templates.ts — modal HTML templates and ModalContent type
 *   app-project.ts     — project/session CRUD handlers
 *   app-mesh.ts        — mesh I/O and transfer handlers
 *   app-modal.ts       — modal management handlers
 */

// ============================================================================
// Component side-effect imports
// ============================================================================
import '../overlays/as-modal-overlay.ts';
import '../error/as-error-info.ts';
import '../overlays/as-notifications.ts';
import '../overlays/as-modal-unsaved-warning.ts';
import '../overlays/as-modal-save-project.ts';
import '../overlays/as-modal-restore-session.ts';
import '../overlays/as-modal-expired-session.ts';
import '../overlays/as-modal-paramate-error.ts';
import '../inspector/as-inspector.ts';
import '../../logic/importExport/exportProgressPlugin.ts';

// ============================================================================
// Store / data
// ============================================================================
import { liveAppData, type IconListing } from '../../store/store.ts';
import { setAsApp } from '../../data/app-ref.ts';
import { deleteAllStoredTransfers, deleteAllProjectsForApp, getProjectsForApp } from '../../logic/db.ts';
import type { AsModalSaveProject } from '../overlays/as-modal-save-project.ts';

// ============================================================================
// API / utilities
// ============================================================================
import { getUserData } from '../../logic/asApi.ts';
import { setHistoryEnabled } from '../../logic/undoHistory.ts';
import { fullAppSuiteRootURL, isProduction, localDev, inIframe, noAPI, suppressExternalServices } from '../../data/constants.ts';
import { detectDeviceInfo, devlog, dispatchCustomEv, navigateViaAnchor, UAContainsMobileKeywords } from '../../logic/utils.ts';
import styleText from './as-app.css?raw';
import modalStyleText from './modal-templates.css?raw';

// ============================================================================
// Sibling modules
// ============================================================================
import { appInfo, getFilteredAppsSync, appList, filterCategories, getAppInfo, getAppFolderName } from './app-config.ts';
import * as ProjectHandlers from './app-project.ts';
import * as MeshHandlers from './app-mesh.ts';
import * as ModalHandlers from './app-modal.ts';

// ============================================================================
// Mixins
// ============================================================================
import { composeMixins } from './mixins/mixin-types-utils.ts';
import { LocalizationMixin } from './mixins/localization-mixin.ts';
import { VersionMixin } from './mixins/version-mixin.ts';
import { InitQueueMixin } from './mixins/init-queue-mixin.ts';
import { ModalMixin } from './mixins/modal-mixin.ts';
import { ErrorMixin } from './mixins/error-mixin.ts';
import { ProjectMixin } from './mixins/project-mixin.ts';
import { HistoryMixin } from './mixins/history-mixin.ts';
import { MeshMixin } from './mixins/mesh-mixin.ts';
import { FileDropMixin } from './mixins/file-drop-mixin.ts';
import { BusyMixin } from './mixins/busy-mixin.ts';
import { GrabCADMixin } from './mixins/grabcad-mixin.ts';
import { fetchIconCfg } from './fetch-icon-cfg.ts';
import type { LocalizationMixinMembers } from './mixins/localization-mixin.ts';
import type { VersionMixinMembers } from './mixins/version-mixin.ts';
import type { InitQueueMixinMembers } from './mixins/init-queue-mixin.ts';
import type { ModalMixinMembers } from './mixins/modal-mixin.ts';
import type { ErrorMixinMembers } from './mixins/error-mixin.ts';
import type { HistoryMixinMembers } from './mixins/history-mixin.ts';
import type { ProjectMixinMembers } from './mixins/project-mixin.ts';
import type { MeshMixinMembers } from './mixins/mesh-mixin.ts';
import type { FileDropMixinMembers } from './mixins/file-drop-mixin.ts';
import type { BusyMixinMembers } from './mixins/busy-mixin.ts';
import type { GrabCADMixinMembers } from './mixins/grabcad-mixin.ts';
import { ExportBuilder } from '../../logic/importExport/exportBuilder.ts';
import type { ExportBuilderOptions } from '../../logic/importExport/exportBuilder.ts';
import * as FileSave from '../../logic/fileSave.ts';

devlog('[CONSTANTS] LocalDev', localDev, 'Production?', isProduction);

// ============================================================================
// Type definitions
// ============================================================================

declare global {
  interface Window {
    ParamateConfigurator: any;
    ParamateViewportWebGL: any;
  }
}

export interface GetMeshEvent extends CustomEvent<{
  resolve: Function;
  reject: Function;
  finalized?: boolean;
  filename?: string;
  type?: string;
}> {}

// ============================================================================
// Mixin composition
// ============================================================================

const AsAppBase = composeMixins(
  HTMLElement,
  LocalizationMixin,
  VersionMixin,
  InitQueueMixin,
  ModalMixin,
  ErrorMixin,
  ProjectMixin,
  HistoryMixin,
  MeshMixin,
  FileDropMixin,
  BusyMixin,
  GrabCADMixin,
);

// ============================================================================
// AsApp component
// ============================================================================

export interface AsApp extends
  LocalizationMixinMembers,
  VersionMixinMembers,
  InitQueueMixinMembers,
  ModalMixinMembers,
  ErrorMixinMembers,
  HistoryMixinMembers,
  ProjectMixinMembers,
  MeshMixinMembers,
  FileDropMixinMembers,
  BusyMixinMembers,
  GrabCADMixinMembers {}

export class AsApp extends AsAppBase {
  static tag = 'as-app'

  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private static readonly modalSheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(modalStyleText);
    return sheet;
  })();

  public appId: string;
  public mobileDetected: boolean | undefined = undefined;

  get appInfo() { return appInfo; }
  get appList() { return appList; }
  get filterCategories() { return filterCategories; }
  get isProduction() { return isProduction; }

  /**
   * Returns a promise that resolves with the app info once it is loaded.
   */
  public async getAppInfo(): Promise<any> {
    return getAppInfo();
  }
  get simpleProjects(): boolean { return isProduction; }
  get otherAppsForTransfer(): string[] {
    const currentAppCfg = appInfo[this.appId];
    const exclude = currentAppCfg?.allowSelfTransfer ? [] : [this.appId];
    return getFilteredAppsSync('all', true, exclude);
  }

  static get observedAttributes(): string[] {
    return ['onprem', 'env', 'lang', 'app-id', 'server-root', 'assets-path', 'show-debug', 'debug-log-notifications', 'disable-session-handlers', 'use-local-assets', 'file-droppable', 'disable-undo-history'];
  }

  // ==========================================================================
  // Init
  // ==========================================================================

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsApp.sheet,
      AsApp.modalSheet,
    ];
    this.appId = this.getAttribute('app-id') ?? 'invalid';

    // Environment Handling
    this._initEnvironment();

    const serverRoot = this.getAttribute('server-root');
    const assetsPath = this.getAttribute('assets-path');
    if (serverRoot) liveAppData.serverRoot = serverRoot;
    if (assetsPath) liveAppData.assetsPath = assetsPath;
    if (this.hasAttribute('use-local-assets')) liveAppData.useLocalAssets = true;

    this.shadowRoot && (
      this.shadowRoot.innerHTML = /*html*/`
        <div id="as-app-container">
          <slot></slot>
        </div>
        <as-inspector id="inspector" ${isProduction ? 'disabled' : ''} ${this.hasAttribute('show-debug') ? 'show-debug' : ''}></as-inspector>
        <as-notifications default-timeout="5000"></as-notifications>
        <as-modal-overlay id="as-app-overlay" mode="auto" visible-child="unsaved-warning">
          <as-error-info id="main-error-panel"></as-error-info>
          <as-mobile-warning id="mobile-warning"></as-mobile-warning>
          <as-modal-unsaved-warning id="unsaved-warning" disable-bg-close></as-modal-unsaved-warning>
          <as-modal-save-project id="save-project"></as-modal-save-project>
          <as-modal-expired-session id="expired-session" disable-bg-close></as-modal-expired-session>
        </as-modal-overlay>
      `
    );
  }

  // ==========================================================================
  // Connected / disconnected
  // ==========================================================================

  private _closing = false;

  /**
   * Actually quit. In the GrabCAD/Electron host the real close runs through the
   * host-provided `onQuit` callback — bare `window.close()` is a no-op inside the
   * webview — so we mirror the host pattern `onQuit(() => window.close())`, and
   * fall back to `window.close()` elsewhere. `_closing` latches so the
   * closeRequested / beforeunload handlers let the ensuing close pass through
   * instead of re-prompting.
   */
  private _quit(): void {
    this._closing = true;
    const doClose = () => window.close();
    const onQuit = (window as any).onQuit;
    typeof onQuit === 'function' ? onQuit(doClose) : doClose();
    // In the GrabCAD/Electron webview window.close() is a no-op and onQuit may
    // decline to tear us down; if we're still alive a moment later, un-latch so
    // the next close attempt is intercepted again instead of silently passing
    // through. A permanently-stuck latch is why the unsaved warning only
    // appeared sporadically — once any non-closing quit ran, every subsequent
    // closeRequested/beforeunload bailed at the `_closing` guard.
    setTimeout(() => { this._closing = false; }, 1000);
  }

  private _onCloseApp = (event: Event): void => {
    if ((event as CustomEvent).detail?.save) {
      this.pendingAction = 'save-and-close';
      this.dispatchEvent(new CustomEvent('save-session'));
    } else {
      this._quit();
    }
  };

  /**
   * Primary window-close intercept. The GrabCAD/Electron host dispatches a
   * cancelable `closeRequested` when the window's close button is clicked and
   * exposes `onQuit` for the real quit. We cancel the close and, when there are
   * unsaved changes, show the two-phase unsaved-warning modal (which quits via
   * `_quit()` once the user confirms); with nothing to save we quit straight away.
   *
   * If a configure round-trip or export is in flight we hold the close first,
   * show a "finishing up" view in the exit modal, and wait for the app to go
   * idle before deciding — closing mid-configure abandons the Paramate request
   * and can drop the last edit's ground-truth sync (see `_waitForBusyIdle`).
   */
  private _onCloseRequested = async (event: Event): Promise<void> => {
    if (this._closing) return;
    (event as any).preventDefault();      // cancel the host's close
    if (this.isSaving) return;            // a save is mid-flight — stay

    if (this._isAppBusy()) {
      this._showExitWaiting();
      await this._waitForBusyIdle();
      this.modalOverlay?.querySelector('#unsaved-warning')?.removeAttribute('waiting');
      // Re-check: a save may have kicked off while we waited.
      if (this._closing || this.isSaving) return;
      // Dismiss the waiting spinner before deciding — quitting closes the
      // overlay outright; the unsaved path reopens it in phase 1.
      this.modalOverlay?.close();
    }

    if (!this.unsavedChanges) { this._quit(); return; }
    this._openExitWarning('close');
  };

  /**
   * Fallback window-close intercept for hosts/browsers that fire `beforeunload`
   * but NOT the host's `closeRequested` (when closeRequested fires, its
   * preventDefault cancels the unload and this never runs). Gated to the GrabCAD
   * host so a plain browser doesn't pop its native "Leave site?" dialog on top
   * of our modal.
   *
   * `beforeunload` is synchronous and cannot await, so an in-flight
   * configure/export can only be *blocked* here — cancel the close and let the
   * user retry once idle, at which point the closeRequested path handles it
   * cleanly.
   */
  private _onBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (this._closing) return;            // already committed to closing
    if (!this.isInGrabCAD()) return;
    if (!this.unsavedChanges && !this._isAppBusy()) return;
    event.preventDefault();
    event.returnValue = 'unsaved-changes';  // truthy value cancels the close in the host
  };

  /**
   * Synchronous snapshot of the app's ref-counted busy state (BusyMixin). The
   * `as-get-app-busy` handler resolves its callback inline, so we can read the
   * value in the same tick — needed by the synchronous `beforeunload` guard.
   */
  private _isAppBusy(): boolean {
    let busy = false;
    window.dispatchEvent(new CustomEvent('as-get-app-busy', {
      detail: { resolve: (detail: { busy: boolean }) => { busy = detail.busy; } }
    }));
    return busy;
  }

  /**
   * Resolve once the app's busy state clears (no in-flight configure/export).
   * Resolves immediately if already idle. A safety timeout aborts a stuck
   * Paramate request via the configurator's `cancelLastRequest()` rather than
   * letting the close hang forever.
   */
  private _waitForBusyIdle(timeoutMs = 30000): Promise<void> {
    if (!this._isAppBusy()) return Promise.resolve();

    return new Promise<void>(resolve => {
      let timer: ReturnType<typeof setTimeout>;
      const done = () => {
        window.removeEventListener('as-app-busy', onBusy as EventListener);
        clearTimeout(timer);
        resolve();
      };
      const onBusy = (ev: CustomEvent<{ busy: boolean }>) => {
        if (ev.detail?.busy === false) done();
      };
      window.addEventListener('as-app-busy', onBusy as EventListener);
      timer = setTimeout(() => {
        // Abort whatever's stuck so we don't hold the window open indefinitely.
        this.querySelector<any>('as-configurator')?.configurator?.cancelLastRequest?.();
        done();
      }, timeoutMs);
    });
  }

  /**
   * Show the exit modal's "finishing up" waiting view. Skipped when the overlay
   * is already showing the export-progress modal, which reports its own progress
   * — no point covering it with a generic spinner.
   */
  private _showExitWaiting(): void {
    if (this.modalOverlay?.hasAttribute('show') &&
        this.modalOverlay.getAttribute('visible-child') === 'export-progress') {
      return;
    }
    ModalHandlers.openModal(this as any, 'unsaved-warning', 'as-modal-unsaved-warning', { waiting: '' });
    const el = this.modalOverlay?.querySelector('#unsaved-warning');
    el?.removeAttribute('saved');
    el?.removeAttribute('mode');
  }

  /**
   * Open the unsaved-warning modal in its first ("unsaved") phase. `mode` picks
   * the verb (close / home / new); `saved` is cleared so a reused element never
   * reopens in the second phase from a prior interaction.
   */
  private _openExitWarning(mode: 'close' | 'home' | 'new'): void {
    ModalHandlers.openModal(this as any, 'unsaved-warning', 'as-modal-unsaved-warning', { mode });
    const el = this.modalOverlay?.querySelector('#unsaved-warning');
    el?.removeAttribute('saved');
    el?.removeAttribute('waiting');  // never let a stale waiting phase mask phase-1
  }

  /**
   * Carry out the exit the unsaved-warning modal was opened for. Shared by the
   * confirm-exit-app button (phase-1 discard / phase-2 already-saved) and the
   * one-step save-and-exit that follows a successful trackable save, so a saved
   * exit doesn't require a second click. `mode` is the modal's verb: home
   * redirects to the storefront, new starts a fresh project, close quits.
   */
  private _performExit(mode: string | null | undefined): void {
    this.isHomeRedirectPending = false;
    this.pendingAction = null;
    this.modalOverlay?.close();
    
    // Discard tracking so the ensuing navigation/close isn't intercepted
    // by the beforeunload handler as a second "unsaved" warning.
    this.unsavedChanges = false;

    if (mode === 'home') {
      this._redirectToStorefront();
    } else if (mode === 'new') {
      ProjectHandlers.createAndSetNewProject(this as any);
    } else if (mode === 'close') {
      this._quit();
    }
  }

  async connectedCallback() {
    setAsApp(this);
    await super.connectedCallback?.();
    
    if (window.asGlobals) {
      window.asGlobals.triggerExpiry = async () => {
        const configuratorEl = this.querySelector<any>('as-configurator');
        if (configuratorEl?.initialized) {
          if (!this.currentProject?.paramateSession) {
            try {
              this.currentProject!.paramateSession = await configuratorEl.getSessionBlob();
            } catch (e) {
              console.error('Failed to get session blob', e);
            }
          }
          ProjectHandlers.handleSessionExpired(this as any, 31 * 60 * 1000);
        }
      };
      
      window.asGlobals.invalidateSession = () => {
        const configuratorEl = this.querySelector<any>('as-configurator');
        if (configuratorEl?.configurator) {
          configuratorEl.configurator.configSession = 'invalid-session-id';
          console.log('Session invalidated. The next configurator action will fail.');
        }
      };

      /**
       * Simulate the machine sleeping past the session TTL with the tab visible
       * (lid closed on an active tab): capture the session blob for the restore
       * offer, pause the keepalive so the server session genuinely expires, then
       * after `minutes` fire the overdue heartbeat — which is what the browser
       * does on resume. The resulting 404 must route into the expired-session
       * flow (as-session-lost -> handleSessionExpired), NOT the global error modal.
       *
       * Real-expiry test:   asGlobals.simulateSleep()      — wait 32 min for the
       *                     server session to actually die.
       * Instant test:       asGlobals.simulateSleep(0.1);  — then, within ~6s:
       *                     asGlobals.invalidateSession()  — heartbeat fires
       *                     against the invalidated session id.
       */
      window.asGlobals.simulateSleep = async (minutes = 32) => {
        const configuratorEl = this.querySelector<any>('as-configurator');
        if (!configuratorEl?.initialized) {
          console.warn('[SIMULATE-SLEEP] Configurator not initialized');
          return;
        }
        // Capture while the session is still alive — the modal's Restore needs it.
        if (!this.currentProject?.paramateSession) {
          try {
            this.currentProject!.paramateSession = await configuratorEl.getSessionBlob();
          } catch (e) {
            console.error('[SIMULATE-SLEEP] Failed to capture session blob', e);
          }
        }
        configuratorEl.pauseSessionHeartbeat();
        console.log(`[SIMULATE-SLEEP] Heartbeat paused; overdue keepalive fires in ${minutes} min`);
        setTimeout(() => {
          console.log('[SIMULATE-SLEEP] Firing overdue keepalive against the expired session');
          configuratorEl.triggerHeartbeat();
        }, minutes * 60 * 1000);
      };
    }

    fetchIconCfg(this);

    for (const desc of ExportBuilder.getModalDescriptors()) {
      if (!this.modalOverlay?.querySelector(`#${desc.id}`)) {
        const el = document.createElement(desc.tagName);
        el.id = desc.id;
        if (desc.disableBgClose) el.setAttribute('disable-bg-close', '');
        this.modalOverlay?.appendChild(el);
      }
    }

    window.addEventListener('as-close-app', this._onCloseApp);
    window.addEventListener('closeRequested', this._onCloseRequested);
    window.addEventListener('beforeunload', this._onBeforeUnload);

    if (localDev && !suppressExternalServices) {
      getUserData().then(res => { this.userData = res.userData; this._initGrabCADEnv(); });
    } else if (!inIframe && !noAPI && !suppressExternalServices) {
      getUserData().then(async res => {
        this.userData = res.userData;
        this._initGrabCADEnv();
        //@ts-ignore
        const stopRedir = window?.stopRedir;
        if (stopRedir) {
          devlog('User data response:', res.userData);
        } else if (!this.hasAttribute('dev-mode') && !res.isAuthenticated) {
          // Redirect by deployment folder name, not app id: after sign-in the
          // storefront lands the user on `/apps/{redirApp}`, and the app id can
          // drift out of sync with the folder name (see getAppFolderName).
          const redirApp = await getAppFolderName(this.appId);
          window.location.href = `${fullAppSuiteRootURL}/?modal=sign-in-form&redirApp=${encodeURIComponent(redirApp)}`;
        }
      });
    } else if (suppressExternalServices) {
      getUserData().then(res => { this.userData = res.userData; this._initGrabCADEnv(); });
    }

    if (this.appId === 'invalid' || this.appId === 'template') {
      alert('app-id is required attribute on as-app');
    } else {
      liveAppData.appId = this.appId;
      // Download gating: the appList reports per-app downloadEnabled
      // (license-gated). Missing entries or an unavailable list leave
      // canDownload at its default (true).
      getAppInfo().then(info => {
        liveAppData.canDownload = info?.[this.appId]?.downloadEnabled !== false;
      }).catch(() => {});
    }

    this.sendLog(this.appId === 'storefront' ? 'storefront_opened' : 'app_opened', { browser: navigator.userAgent });

    this.addEventListener('param-change', this.handleParamChange as EventListener);
    
    window.addEventListener('as-button-click', this._handleButtonClick as EventListener);
    window.dispatchEvent(new CustomEvent('as-app', { detail: { sender: this } }));

    const deviceInfo = detectDeviceInfo();
    if (deviceInfo.hasTouchScreen) {
      if (this.hasAttribute('mobile-view')) {
        console.warn('Custom mobile view is not implemented yet.');
      }
      devlog('[MOBILE]', UAContainsMobileKeywords());
      if (UAContainsMobileKeywords()) this.mobileDetected = true;
      if (UAContainsMobileKeywords() && this.appId !== 'storefront') {
        dispatchCustomEv(this.modalOverlay!, 'as-set-visible-child', { id: 'mobile-warning' });
        dispatchCustomEv(this.modalOverlay!, 'as-open-modal');
      }
    }
  }

  disconnectedCallback(): void {
    this.removeEventListener('param-change', this.handleParamChange as EventListener);
    window.removeEventListener('as-button-click', this._handleButtonClick as EventListener);
    window.removeEventListener('as-close-app', this._onCloseApp);
    window.removeEventListener('closeRequested', this._onCloseRequested);
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    super.disconnectedCallback?.();
  }

  /**
   * Directly initiates a mesh transfer to another application.
   * This is useful for automated workflows like autoShadowBoard.
   */
  public transferMesh(destinationAppId: string): void {
    MeshHandlers.autoTransferToApp(this as any, destinationAppId);
  }

  /**
   * Save an arbitrary file to disk using the mechanism appropriate for the
   * current environment (e.g. GrabCAD's native save dialog when hosted there,
   * a browser download otherwise). Resolves once the host reports the result —
   * await it before e.g. closing the window. success is false when the user
   * cancelled the native dialog.
   *
   * For exporting files, use ExportBuilder.download() instead — it resolves
   * the builder, handles single-file/ZIP packaging, and routes through the
   * same environment-aware save.
   */
  public saveFileAs(blob: Blob, filename: string, opts: FileSave.FileSaveOptions = {}): Promise<FileSave.FileSaveResult> {
    return FileSave.saveFileAs(blob, filename, opts);
  }

  /**
   * Quick helper to download the current configurator mesh directly.
   * If only one file is resolved, it downloads directly; otherwise zipped.
   */
  public async downloadCurrentMesh(
    fileName: string,
    finalize = true,
    opts: ExportBuilderOptions = {}
  ): Promise<void> {
    const builder = this.createExporter(opts);
    builder.addConfiguratorMesh({ fileName, customData: { finalize } });
    await builder.download({ filename: fileName });
  }

  // ==========================================================================
  // Button click handler
  // ==========================================================================

  private _handleButtonClick = async (event: CustomEvent) => {
    const { buttonName } = event.detail;
    switch (buttonName) {
      case 'transfer-mesh':
        MeshHandlers.prepareTransferMesh(this as any);
        break;
      case 'home':
        // Lean on unsavedChanges as the single source of truth — independent of
        // project mode (apps set this flag directly or via as-input-change).
        if (this.unsavedChanges) {
          this.pendingAction = 'home';
          this._openExitWarning('home');
        } else {
          this._redirectToStorefront();
        }
        break;
      case 'info':
        if (!this.simpleProjects) window.dispatchEvent(new CustomEvent('as-inspector-toggle', { bubbles: true }));
        break;
      case 'open-modal':
        this.modalOverlay?.open('main-error-panel');
        break;
      case 'cancel-exit-app':
        // Stay put. cancel-exit-app only originates from the unsaved-warning modal.
        this.isHomeRedirectPending = false;
        this.pendingAction = null;
        this.modalOverlay?.close();
        break;
      case 'save-before-exit': {
        if (this.hasAttribute('disable-session-handlers')) {
          // Managed saving is off — hand the save to the host via the documented
          // save-and-close hook (same delegation as handleSaveSession) instead of
          // downloading the internal .asproject format the host can't restore.
          // The host save can't report completion, so advance to phase 2 like an
          // untrackable download and let the user decide when to leave.
          ProjectHandlers.delegateSaveToHost(this as any);
          this.modalOverlay?.querySelector('#unsaved-warning')?.setAttribute('saved', '');
          break;
        }
        const saved = await ProjectHandlers.saveCurrentProject(this as any);
        if (FileSave.isSaveTrackable()) {
          // Trackable provider (GrabCAD native dialog reports its result): finish
          // in one step. A cancelled save keeps us in phase 1 so no work is lost;
          // a successful save exits immediately — no redundant "saved, leave when
          // ready" second click. See fileSave.ts (isSaveTrackable).
          if (!saved) break;
          this._performExit(this.modalOverlay?.querySelector('#unsaved-warning')?.getAttribute('mode'));
          break;
        }
        // Untrackable browser <a download>: we can't detect when the download
        // finished, so advance to phase 2 and let the user choose when to leave.
        this.modalOverlay?.querySelector('#unsaved-warning')?.setAttribute('saved', '');
        break;
      }
      case 'confirm-exit-app': {
        // Proceed with the exit the unsaved-warning modal was opened for. Same
        // action in both phases: phase 1 discards unsaved work, phase 2 has
        // already saved it. Driven by the modal's `mode` so it stays independent
        // of the pendingAction save-and-close machine (the GrabCAD-success modal).
        this._performExit(this.modalOverlay?.querySelector('#unsaved-warning')?.getAttribute('mode'));
        break;
      }
      // 'header-help-button' is intentionally not handled here: the header now
      // renders the help button as a real <a target="_blank"> to the clickthru-url
      // (see as-header.ts). A genuine anchor click navigates even in embedded
      // webviews (e.g. GrabCAD Print) that block scripted window.open.
      case 'confirm-save-project': {
        const saveModalEl = this.modalOverlay?.querySelector('#save-project') as AsModalSaveProject;
        const projectName = saveModalEl?.getValue() || 'Untitled';
        ProjectHandlers.saveCurrentProject(this as any, projectName).then((saved) => {
          ProjectHandlers.closeSaveProjectModal(this as any);
          // Only follow through with the pending exit on a real save. A cancelled
          // trackable save (see fileSave.ts) aborts it so no work is lost to a
          // dismissed Save dialog; a browser <a download> reports success.
          if (!saved) { this.pendingAction = null; return; }
          if (this.pendingAction === 'new-session') {
            ProjectHandlers.createAndSetNewProject(this as any);
            this.pendingAction = null;
          } else if (this.pendingAction === 'home') {
            this._redirectToStorefront();
            this.pendingAction = null;
          } else if (this.pendingAction === 'save-and-close') {
            this.pendingAction = null;
            this._quit();
          }
        });
        break;
      }
      case 'cancel-save-project':
        ProjectHandlers.closeSaveProjectModal(this as any);
        this.pendingAction = null;
        break;
      case 'restore-unsaved-session':
        if (this.unsavedSessionProject) {
          const projectToRestore = this.unsavedSessionProject;
          this.unsavedSessionProject = null;
          this.modalOverlay?.close();
          this.addToPostInitQueue(() => ProjectHandlers.loadProject(this as any, projectToRestore));
        }
        break;
      case 'discard-unsaved-session':
        if (this.unsavedSessionProject) {
          getProjectsForApp(this.appId).then(projects => {
            projects.filter(p => !p.metadata.savedAt).forEach(p => {
              import('../../logic/db.ts').then(db => db.deleteProject(p.metadata.id));
            });
          });
          this.unsavedSessionProject = null;
        }
        this.modalOverlay?.close();
        break;
      case 'restore-expired-session': {
        const projectToRestore = this.currentProject;
        const configuratorEl = this.querySelector<any>('as-configurator');
        this.modalOverlay?.close();
        if (projectToRestore?.paramateSession && configuratorEl?.initialized) {
          try {
            // The previous server session expired while the machine was suspended, so
            // restoring the blob into the old session id 404s. Establish a fresh session
            // first, then load the captured project the same way tab-restore does — via
            // loadProject, whose restoreSession now targets a live session.
            await configuratorEl.refreshSession();
            await ProjectHandlers.loadProject(this as any, projectToRestore);
          } catch (err) {
            console.error('[RESTORE] Failed to restore expired session:', err);
            this.dispatchEvent(new CustomEvent('as-notify', { detail: { defaultText: 'Failed to restore session', type: 'error' } }));
          }
        }
        break;
      }
      case 'dismiss-paramate-error':
        this.modalOverlay?.close();
        break;
      case 'discard-expired-session':
        ProjectHandlers.createAndSetNewProject(this as any);
        this.modalOverlay?.close();
        break;
      default:
        break;
    }

    const buttonToLogEvName: Record<string, string> = {
      'new': 'new_session',
      'restore': 'restore_session',
      'save': 'save_session',
    };
    const evName = buttonToLogEvName[buttonName];
    if (evName) this.sendLog(evName);
  };

  // ==========================================================================
  // Language / localization
  // ==========================================================================

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    switch (name) {
      case 'lang':         this._loc.updateLang(newValue); break;
      case 'app-id':       this.appId = newValue; break;
      case 'server-root':  if (newValue) liveAppData.serverRoot = newValue; break;
      case 'assets-path':  if (newValue) liveAppData.assetsPath = newValue; break;
      case 'show-debug':   this.updateDebugPanel(); break;
      case 'file-droppable':
        newValue !== null ? this._enableFileDrop() : this._disableFileDrop();
        break;
      case 'disable-session-handlers':
        liveAppData.defaultProjectHandlingEnabled = newValue === null;
        break;
      case 'disable-undo-history':
        setHistoryEnabled(newValue === null);
        break;
      case 'env':
        liveAppData.appEnv = newValue ?? null;
        break;
    }
  }

  // ==========================================================================
  // Component store
  // ==========================================================================

  public initComponentStore(opts: {
    serverRoot?: string;
    assetsPath?: string;
    appLangFileName?: string;
    additionalIconPaths?: IconListing;
  }) {
    Object.entries(opts).forEach(([key, value]) => {
      switch (key) {
        case 'serverRoot':          liveAppData.serverRoot = value; break;
        case 'assetsPath':          liveAppData.assetsPath = value; break;
        case 'appLangFileName':     liveAppData.appLangFileName = value; break;
        case 'lang':                this._loc.updateLang(value as string); break;
        case 'additionalIconPaths':
          liveAppData._iconList = { ...liveAppData._iconList, ...value as IconListing };
          break;
      }
    });
  }

  // ==========================================================================
  // Ctrls & params
  // ==========================================================================

  registerCustomControls(ctrlRefs: { id: string; get: () => {} }[]) {
    ctrlRefs.forEach(_info => { /* TODO */ });
  }

  private handleParamChange(event: CustomEvent): void {
    devlog('Handling param change event', event);
    // TODO: app store not currently used
  }

  // ==========================================================================
  // Misc
  // ==========================================================================

  private _initEnvironment(): void {
    // Evict any stale cached env from the old 24h-localStorage scheme.
    localStorage.removeItem('as-app-env');

    const urlParams = new URLSearchParams(window.location.search);
    const env = urlParams.get('env') ?? this.getAttribute('env');

    if (env) {
      this.setAttribute('env', env);
      if (env === 'onprem') this.setAttribute('onprem', '');
    }
    liveAppData.appEnv = env ?? null;
  }

  private _redirectToStorefront(): void {
    this.isHomeRedirectPending = false;
    devlog('[as-app] _redirectToStorefront -> navigating to', fullAppSuiteRootURL);
    // Anchor click rather than `window.location.href` so Exit-to-storefront also
    // works in the GrabCAD embedded webview (see navigateViaAnchor). Deferred to a
    // fresh task: the discard path calls this synchronously inside the button's
    // click dispatch, and an anchor click nested in that dispatch (right after the
    // dialog closes) is dropped by the webview. The save path only worked because
    // its `await` already escaped the dispatch — this gives the discard path the
    // same clean task.
    setTimeout(() => navigateViaAnchor(fullAppSuiteRootURL), 0);
  }

  clearTransferDB() { return deleteAllStoredTransfers(); }

  async clearProjectsDB() {
    const count = await deleteAllProjectsForApp(this.appId);
    await ProjectHandlers.refreshProjectsList(this as any);
    console.log(`Cleared ${count} projects for app ${this.appId}`);
    return count;
  }

}
